// 2025-04-23-device.ts
// Device detection and context utilities
/**
 * Comprehensive mobile device detection
 * Combines user agent detection, touch capability, and viewport size
 */
export function detectMobileDevice() {
    if (typeof navigator === "undefined" || typeof window === "undefined") {
        return false;
    }
    const nav = navigator;
    const win = window;
    const ua = (nav && (nav.userAgent || nav.vendor)) || (win && win.opera) || "";
    // User agent based detection
    const uaMatchesMobile = /Mobile|Android|iP(ad|hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)|Windows Phone|Phone|Tablet/i.test(ua);
    // Touch capability detection
    const touchPoints = (nav && nav.maxTouchPoints) || 0;
    const isTouchCapable = touchPoints > 1;
    // Viewport detection
    const narrowViewport = win
        ? Math.min(win.innerWidth || 0, win.innerHeight || 0) <= 820
        : false;
    return uaMatchesMobile || (isTouchCapable && narrowViewport);
}
/**
 * Get detailed device information
 */
export function getDeviceInfo() {
    const isMobile = detectMobileDevice();
    const nav = navigator;
    const win = window;
    const touchPoints = (nav && nav.maxTouchPoints) || 0;
    const isTouchCapable = touchPoints > 1;
    // Use clientWidth instead of innerWidth to exclude scrollbars
    const screenWidth = typeof document !== "undefined"
        ? document.documentElement.clientWidth
        : win?.innerWidth || 0;
    const screenHeight = typeof document !== "undefined"
        ? document.documentElement.clientHeight
        : win?.innerHeight || 0;
    const isTablet = isMobile && Math.min(screenWidth, screenHeight) >= 600;
    return {
        isMobile,
        isTablet,
        isDesktop: !isMobile,
        isTouchCapable,
        deviceType: isMobile ? (isTablet ? "tablet" : "mobile") : "desktop",
        userAgent: (nav && (nav.userAgent || nav.vendor)) || "",
        screenWidth,
        screenHeight,
    };
}
/**
 * Initialize device detection and log to console
 */
export function initDeviceDetection() {
    const deviceInfo = getDeviceInfo();
    // Calculate and set scaling factor for mobile
    if (deviceInfo.isMobile && typeof document !== "undefined") {
        // Design width: 280px (14 columns × 20px)
        const designWidth = 280;
        const actualWidth = deviceInfo.screenWidth;
        const scalingFactor = actualWidth / designWidth;
        // Set CSS custom property for scaling
        document.documentElement.style.setProperty("--scaling-factor-mobile", scalingFactor.toFixed(3));
        console.log(`[DS one] Mobile device detected - ${deviceInfo.deviceType} (${deviceInfo.screenWidth}x${deviceInfo.screenHeight}), scaling factor: ${scalingFactor.toFixed(2)}`);
    }
    else {
        // Desktop - no scaling
        if (typeof document !== "undefined") {
            document.documentElement.style.setProperty("--scaling-factor-mobile", "1");
        }
        console.log(`[DS one] Desktop device detected (${deviceInfo.screenWidth}x${deviceInfo.screenHeight})`);
    }
    // Log additional details in development mode
    if (typeof window !== "undefined" && window.DS_ONE_DEBUG) {
        console.log("[DS one] Device Info:", {
            type: deviceInfo.deviceType,
            isMobile: deviceInfo.isMobile,
            isTablet: deviceInfo.isTablet,
            isDesktop: deviceInfo.isDesktop,
            isTouchCapable: deviceInfo.isTouchCapable,
            viewport: `${deviceInfo.screenWidth}x${deviceInfo.screenHeight}`,
            userAgent: deviceInfo.userAgent,
        });
    }
    return deviceInfo;
}
// Auto-initialize when module loads
if (typeof window !== "undefined") {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => {
            initDeviceDetection();
        });
    }
    else {
        // DOM is already ready
        initDeviceDetection();
    }
    // Recalculate on resize (debounced)
    let resizeTimeout;
    window.addEventListener("resize", () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            initDeviceDetection();
        }, 100);
    });
}
